import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Box, Stack, Typography, IconButton, Divider, Chip, Card, CardContent, Grid, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, FormControl, FormControlLabel, InputLabel, Select, MenuItem, Button, Tooltip, Popover, RadioGroup, Radio } from "@mui/material";
import { ArrowBack, Percent, CheckCircle, Cancel, Alarm, EventNote, RestartAlt, FileDownload as FileDownloadIcon, PictureAsPdf as PdfIcon, TableChart as ExcelIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from "react-router-dom";
import { getData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";
import Loader from "../../../common/spinner";
import { format } from "date-fns";

function EmployeeAttendanceHistory() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const userId = searchParams.get("id");
    const empId = searchParams.get("empId");
    const empName = searchParams.get("empName");

    // --- 1. Constants & Memoized Data (Define first) ---
    const months = useMemo(() => [
        { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
        { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
        { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
        { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" }
    ], []);

    const years = useMemo(() => Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i), []);

    // --- 2. State Declarations (Define second) ---
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState("All");
    const [monthlyParams, setMonthlyParams] = useState({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        format: ""
    });
    const [overview, setOverview] = useState({
        attendanceRate: "0%",
        presentCount: 0,
        absentCount: 0,
        lateCount: 0,
        leaveCount: 0,
        workFromHomeCount: 0,
        totalWorkingDays: 0
    });
    const [records, setRecords] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [downloadFormat, setDownloadFormat] = useState("");
    const openDownload = Boolean(anchorEl);

    // --- 3. Helper Functions ---
    const formatTime = (dateString) => {
        if (!dateString || dateString === "-") return "N/A";
        try {
            return format(new Date(dateString), "hh:mm a");
        } catch {
            return "N/A";
        }
    };

    const formatDate = (dateString) => {
        try {
            return format(new Date(dateString), "dd MMM, yyyy");
        } catch {
            return dateString;
        }
    };

    const getDayName = (dateString) => {
        try {
            return format(new Date(dateString), "EEEE");
        } catch {
            return "-";
        }
    };

    // --- 4. Callbacks & Effects (Defined after state) ---
    const fetchAttendanceHistory = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const selectedMonthLabel = months.find(m => m.value === monthlyParams.month)?.label || format(new Date(), "MMMM");

            const params = new URLSearchParams({
                employee_id: userId,
                month: selectedMonthLabel,
                year: monthlyParams.year.toString(),
                status: statusFilter
            }).toString();

            const response = await getData(`${API_ENDPOINTS.getEmployeeMonthlyAttendanceOverviewForHR({})}?${params}`);

            if (response?.status === 'SUCCESS') {
                const data = response.message;
                setOverview({
                    attendanceRate: data.attendanceRate,
                    presentCount: data.presentCount,
                    absentCount: data.absentCount,
                    lateCount: data.lateCount,
                    leaveCount: data.leaveCount,
                    workFromHomeCount: data.workFromHomeCount,
                    totalWorkingDays: data.totalWorkingDays
                });
                setRecords(data.attendanceRecords || []);
            }
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }, [userId, monthlyParams.month, monthlyParams.year, statusFilter, months]);

    useEffect(() => {
        fetchAttendanceHistory();
    }, [fetchAttendanceHistory]);

    // --- 5. Event Handlers ---
    const handleFilterChange = (event) => {
        setStatusFilter(event.target.value);
    };

    const handleResetFilters = () => {
        const currentMonth = new Date().getMonth() + 1;
        const currentYear = new Date().getFullYear();

        setMonthlyParams({
            month: currentMonth,
            year: currentYear,
            format: ""
        });
        setStatusFilter("All");
    };

    const handleDownloadClick = (event) => setAnchorEl(event.currentTarget);

    const handleDownloadClose = () => {
        setAnchorEl(null);
        setDownloadFormat("");
    };

    const handleMonthlyDownload = async () => {
        if (!userId || !downloadFormat) return;

        setLoading(true);
        try {
            const { month, year } = monthlyParams;
            const isPdf = downloadFormat === "pdf";

            const path = isPdf
                ? API_ENDPOINTS.downloadEmployeeDailyAttendancePDF({})
                : API_ENDPOINTS.downloadEmployeeDailyAttendanceExcel({});

            // Fixed the query param logic to use actual downloadFormat selection
            const finalUrl = `${path}?emp_id=${userId}&month=${month}&year=${year}`;

            const response = await getData(finalUrl, { responseType: 'blob' });

            const blob = new Blob([response], {
                type: isPdf ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Monthly_Attendance_${month}_${year}.${isPdf ? "pdf" : "xlsx"}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            handleDownloadClose();
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    };

    const StatCard = ({ title, value, icon, color }) => (
        <Card sx={{
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            height: '100%',
            background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
            color: '#FFFFFF',
        }}>
            <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: '600', opacity: 0.9 }}>
                            {title}
                        </Typography>
                        <Typography variant="h4" fontWeight="800" sx={{ mt: 0.5 }}>
                            {value || 0}
                        </Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', p: 1.5, borderRadius: '12px', display: 'flex' }}>
                        {React.cloneElement(icon, { sx: { color: '#FFFFFF', fontSize: 28 } })}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );

    return (
        <Box sx={{ p: 1 }}>
            {loading && <Loader />}
            <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #c3c3c3', px: 2 }}>
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{ color: '#64748b', borderRadius: '0px', p: 1, '&:hover': { bgcolor: '#e0e3e6' } }}
                >
                    <ArrowBack sx={{ fontSize: 20 }} />
                    <Typography variant="body2" sx={{ ml: 1, fontWeight: 500, color: '#475569' }}>Back</Typography>
                </IconButton>

                <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 24, alignSelf: 'center', background: '#e2e8f0' }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexGrow: 1, py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h5" fontWeight="bold">{empName}</Typography>
                        <Chip label={empId || "N/A"} size="small" variant="outlined" />
                    </Box>
                </Box>
            </Stack>

            <Grid container spacing={3} my={4}>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}><StatCard title="Attendance Rate" value={overview.attendanceRate} icon={<Percent />} color="#6366F1" /></Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}><StatCard title="Present Days" value={overview.presentCount} icon={<CheckCircle />} color="#10B981" /></Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}><StatCard title="Absent Days" value={overview.absentCount} icon={<Cancel />} color="#EF4444" /></Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}><StatCard title="Late Arrivals" value={overview.lateCount} icon={<Alarm />} color="#F59E0B" /></Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}><StatCard title="Leaves" value={overview.leaveCount} icon={<EventNote />} color="#64748B" /></Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>Attendance History</Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                    <FormControl sx={{ minWidth: 150 }} size="small">
                        <InputLabel>Status</InputLabel>
                        <Select name="status" value={statusFilter} label="Status" onChange={handleFilterChange}>
                            <MenuItem value="All">All Status</MenuItem>
                            <MenuItem value="Present">Present</MenuItem>
                            <MenuItem value="Absent">Absent</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 120 }} size="small">
                        <InputLabel>Month</InputLabel>
                        <Select
                            value={monthlyParams.month}
                            label="Month"
                            onChange={(e) => setMonthlyParams(prev => ({ ...prev, month: e.target.value }))}
                        >
                            {months.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 100 }} size="small">
                        <InputLabel>Year</InputLabel>
                        <Select
                            value={monthlyParams.year}
                            label="Year"
                            onChange={(e) => setMonthlyParams(prev => ({ ...prev, year: e.target.value }))}
                        >
                            {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={handleResetFilters} startIcon={<RestartAlt />} sx={{ textTransform: 'none' }}>Reset</Button>
                        <Tooltip title="Export Report">
                            <IconButton onClick={handleDownloadClick} sx={{ border: '1px solid #ddd' }}><FileDownloadIcon color="primary" /></IconButton>
                        </Tooltip>
                    </Box>
                </Stack>
            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', border: '1px solid #E2E8F0' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                            <TableRow>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Date</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Day</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Check-in</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Check-out</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Prod. Hours</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {records.length > 0 ? records.map((row, index) => (
                                <TableRow key={index} hover>
                                    <TableCell>{formatDate(row.date)}</TableCell>
                                    <TableCell>{getDayName(row.date)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.status}
                                            size="small"
                                            color={row.status === "Present" ? "success" : row.status === "LateArrival" ? "warning" : "error"}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>{formatTime(row.check_in)}</TableCell>
                                    <TableCell>{formatTime(row.check_out)}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{row.total_hours || '00:00'}</TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>No records found for this period.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </TableContainer>

            <Popover
                open={openDownload}
                anchorEl={anchorEl}
                onClose={handleDownloadClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Box sx={{ p: 2, width: 220 }}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>Export Format</Typography>
                    <Divider sx={{ mb: 1 }} />
                    <RadioGroup value={downloadFormat} onChange={(e) => setDownloadFormat(e.target.value)}>
                        <FormControlLabel value="pdf" control={<Radio size="small" />} label={<Box display="flex" alignItems="center" gap={1}><PdfIcon sx={{ color: "#ef4444" }} /> PDF</Box>} />
                        <FormControlLabel value="excel" control={<Radio size="small" />} label={<Box display="flex" alignItems="center" gap={1}><ExcelIcon sx={{ color: "#10b981" }} /> Excel</Box>} />
                    </RadioGroup>
                    {downloadFormat && <Button fullWidth variant="contained" size="small" sx={{ mt: 2 }} onClick={handleMonthlyDownload}>Download</Button>}
                </Box>
            </Popover>
        </Box>
    );
}

export default EmployeeAttendanceHistory;